import React, { useState, useEffect } from 'react';
import { authenticatedFetch } from '../utils/auth';
import { generateSearchEvidencePDF } from '../utils/pdfGenerator';
import PdfButton from './common/PdfButton';

const UsageHistory = () => {
  const [history, setHistory] = useState([]);
  const [summary, setSummary] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  // Get Google API key from backend

  const [pdfLoading, setPdfLoading] = useState({});

  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [trendLoading, setTrendLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchData();
    

  }, []);

  useEffect(() => {
    fetchTrendData();
  }, [selectedPeriod]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const [historyResponse, summaryResponse, countResponse] = await Promise.all([
        authenticatedFetch(`/usage-history?page=${currentPage}&limit=${pageSize}`),
        authenticatedFetch('/usage-summary'),
        authenticatedFetch('/usage-history-count')
      ]);

      if (historyResponse.ok && summaryResponse.ok && countResponse.ok) {
        const historyData = await historyResponse.json();
        const summaryData = await summaryResponse.json();
        const countData = await countResponse.json();
        setHistory(historyData);
        setSummary(summaryData);
        setTotalCount(countData.total_count);
        setError(''); // Clear any previous errors
      } else {
        setError('Failed to fetch usage data');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchTrendData = async () => {
    setTrendLoading(true);
    try {
      const response = await authenticatedFetch(`/usage-trend?period=${selectedPeriod}`);
      if (response.ok) {
        const data = await response.json();
        setTrendData(data);
      } else {
        setError('Failed to fetch trend data');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setTrendLoading(false);
    }
  };



  const generateSinglePDF = async (record, recordId) => {
    setPdfLoading(prev => ({ ...prev, [recordId]: true }));
    try {
              await generateSearchEvidencePDF(record);
    } catch (error) {
      setError('Failed to generate PDF evidence');
    } finally {
      setPdfLoading(prev => ({ ...prev, [recordId]: false }));
    }
  };





  const exportAsCSV = () => {
    const filteredHistory = getFilteredHistory();
    if (filteredHistory.length === 0) return;

    const headers = ['Timestamp', 'Address', 'Digipin', 'Action', 'Source', 'Search Type', 'Latitude', 'Longitude'];
    const csvContent = [
      headers.join(','),
      ...filteredHistory.map(record => [
        record.timestamp,
        `"${record.address}"`,
        record.digipin,
        record.action,
        record.source,
        record.search_type || '',
        record.latitude || '',
        record.longitude || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'usage_history.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getFilteredHistory = () => {
    if (!dateFilter.startDate && !dateFilter.endDate) {
      return history.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }

    return history.filter(record => {
      const recordDate = new Date(record.timestamp);
      const startDate = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
      const endDate = dateFilter.endDate ? new Date(dateFilter.endDate + 'T23:59:59') : null;

      if (startDate && endDate) {
        return recordDate >= startDate && recordDate <= endDate;
      } else if (startDate) {
        return recordDate >= startDate;
      } else if (endDate) {
        return recordDate <= endDate;
      }
      return true;
    }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const renderLineChart = () => {
    if (!trendData || !trendData.data || trendLoading) {
      // Show placeholder with animation while loading
      return (
        <div style={{ marginBottom: '30px' }}>
          <h3>Usage Trend</h3>
          <div style={{ marginBottom: '15px' }}>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              disabled={trendLoading}
              style={{
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                opacity: trendLoading ? 0.6 : 1
              }}
            >
              <option value="today">Today</option>
              <option value="this_week">This Week</option>
              <option value="this_month">This Month</option>
              <option value="overall">Overall</option>
            </select>
          </div>
          <div style={{ 
            border: '1px solid #dee2e6', 
            borderRadius: '8px', 
            padding: '20px',
            backgroundColor: 'white',
            height: '240px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}>
            {/* Animated loading placeholder */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '15px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ 
                color: '#666', 
                margin: 0,
                fontSize: '14px'
              }}>
                {trendLoading ? 'Loading chart data...' : 'No data available'}
              </p>
            </div>
            
            {/* Placeholder chart grid */}
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              right: '20px',
              bottom: '20px',
              opacity: '0.1'
            }}>
              <svg width="100%" height="100%">
                {/* Grid lines */}
                {Array.from({ length: 5 }, (_, i) => {
                  const y = 40 + (i * 120) / 4;
                  return (
                    <line
                      key={i}
                      x1="40"
                      y1={y}
                      x2="760"
                      y2={y}
                      stroke="#e9ecef"
                      strokeWidth="1"
                    />
                  );
                })}
                
                {/* Placeholder line */}
                <path
                  d="M 40 160 Q 200 140 400 120 T 760 80"
                  stroke="#007bff"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="5,5"
                  opacity="0.3"
                />
                
                {/* Placeholder data points */}
                {Array.from({ length: 8 }, (_, i) => {
                  const x = 40 + (i * 720) / 7;
                  const y = 160 - (i * 80) / 7;
                  return (
                    <circle
                      key={i}
                      cx={x}
                      cy={y}
                      r="4"
                      fill="#007bff"
                      opacity="0.3"
                    />
                  );
                })}
              </svg>
            </div>
          </div>
          
          {/* CSS for spinner animation */}
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      );
    }

    const maxValue = Math.max(...trendData.data.map(d => d.value));
    const chartHeight = 200;
    const chartWidth = 800;
    const padding = 40;

    const points = trendData.data.map((point, index) => {
      const x = padding + (index * (chartWidth - 2 * padding)) / (trendData.data.length - 1);
      const y = chartHeight - padding - (point.value / maxValue) * (chartHeight - 2 * padding);
      return { x, y, label: point.label, value: point.value };
    });

    const pathData = points.map((point, index) => {
      if (index === 0) return `M ${point.x} ${point.y}`;
      return `L ${point.x} ${point.y}`;
    }).join(' ');

    return (
      <div style={{ marginBottom: '30px' }}>
        <h3>Usage Trend</h3>
        <div style={{ marginBottom: '15px' }}>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="overall">Overall</option>
          </select>
        </div>
        <div style={{ 
          border: '1px solid #dee2e6', 
          borderRadius: '8px', 
          padding: '20px',
          backgroundColor: 'white'
        }}>
          <svg width={chartWidth} height={chartHeight} style={{ maxWidth: '100%' }}>
            {/* Grid lines */}
            {Array.from({ length: 5 }, (_, i) => {
              const y = padding + (i * (chartHeight - 2 * padding)) / 4;
              return (
                <line
                  key={i}
                  x1={padding}
                  y1={y}
                  x2={chartWidth - padding}
                  y2={y}
                  stroke="#e9ecef"
                  strokeWidth="1"
                />
              );
            })}
            
            {/* Line chart */}
            <path
              d={pathData}
              stroke="#007bff"
              strokeWidth="2"
              fill="none"
            />
            
            {/* Data points with hover functionality */}
            {points.map((point, index) => (
              <g key={index}>
                {/* Visible data point */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  fill="#007bff"
                  stroke="#ffffff"
                  strokeWidth="2"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={(e) => {
                    // Remove any existing tooltip first
                    const existingTooltip = document.getElementById('chart-tooltip');
                    if (existingTooltip) {
                      existingTooltip.remove();
                    }
                    
                    const tooltip = document.createElement('div');
                    tooltip.id = 'chart-tooltip';
                    tooltip.style.cssText = `
                      position: fixed;
                      background: rgba(0, 0, 0, 0.9);
                      color: white;
                      padding: 8px 12px;
                      border-radius: 4px;
                      font-size: 12px;
                      pointer-events: none;
                      z-index: 10000;
                      white-space: nowrap;
                      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    `;
                    tooltip.textContent = `${point.label}: ${point.value}`;
                    document.body.appendChild(tooltip);
                    
                    // Position tooltip relative to mouse position
                    const mouseX = e.clientX;
                    const mouseY = e.clientY;
                    
                    // Position tooltip above the mouse cursor
                    tooltip.style.left = `${mouseX - tooltip.offsetWidth / 2}px`;
                    tooltip.style.top = `${mouseY - tooltip.offsetHeight - 10}px`;
                    
                    // Ensure tooltip stays within viewport
                    const tooltipRect = tooltip.getBoundingClientRect();
                    if (tooltipRect.left < 0) {
                      tooltip.style.left = '10px';
                    }
                    if (tooltipRect.right > window.innerWidth) {
                      tooltip.style.left = `${window.innerWidth - tooltip.offsetWidth - 10}px`;
                    }
                    if (tooltipRect.top < 0) {
                      tooltip.style.top = `${mouseY + 20}px`;
                    }
                  }}
                  onMouseLeave={() => {
                    const tooltip = document.getElementById('chart-tooltip');
                    if (tooltip) {
                      tooltip.remove();
                    }
                  }}
                />
              </g>
            ))}
            
            {/* X-axis labels */}
            {points.map((point, index) => (
              <text
                key={index}
                x={point.x}
                y={chartHeight - 10}
                textAnchor="middle"
                fontSize="12"
                fill="#666"
              >
                {point.label}
              </text>
            ))}
            
            {/* Y-axis labels */}
            {Array.from({ length: 5 }, (_, i) => {
              const y = padding + (i * (chartHeight - 2 * padding)) / 4;
              const value = Math.round((maxValue * (4 - i)) / 4);
              return (
                <text
                  key={i}
                  x={padding - 10}
                  y={y + 4}
                  textAnchor="end"
                  fontSize="12"
                  fill="#666"
                >
                  {value}
                </text>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  const renderSummaryTab = () => (
    <div>
      {/* Summary Cards */}
      {summary && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '20px',
          marginBottom: '30px'
        }}>
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#e3f2fd', 
            borderRadius: '8px',
            border: '1px solid #bbdefb',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#1976d2' }}>Today</h3>
            <p style={{ fontSize: '2rem', margin: '0', fontWeight: 'bold' }}>{summary.today}</p>
          </div>
          
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#e8f5e8', 
            borderRadius: '8px',
            border: '1px solid #c8e6c9',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#388e3c' }}>This Week</h3>
            <p style={{ fontSize: '2rem', margin: '0', fontWeight: 'bold' }}>{summary.this_week}</p>
          </div>
          
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#fff3e0', 
            borderRadius: '8px',
            border: '1px solid #ffcc80',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#f57c00' }}>This Month</h3>
            <p style={{ fontSize: '2rem', margin: '0', fontWeight: 'bold' }}>{summary.this_month}</p>
          </div>
          
          <div style={{ 
            padding: '20px', 
            backgroundColor: '#fce4ec', 
            borderRadius: '8px',
            border: '1px solid #f8bbd9',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#c2185b' }}>Overall</h3>
            <p style={{ fontSize: '2rem', margin: '0', fontWeight: 'bold' }}>{summary.overall}</p>
          </div>
        </div>
      )}

      {/* Line Chart */}
      {renderLineChart()}
    </div>
  );

  const renderLogsTab = () => {
    const filteredHistory = getFilteredHistory();
    
    return (
      <div>
        {/* Date Filter Controls */}
        <div style={{ 
          marginBottom: '20px', 
          padding: '20px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3 style={{ margin: 0 }}>Filter Logs</h3>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              style={{
                padding: '8px 16px',
                backgroundColor: refreshing ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: refreshing ? 0.6 : 1
              }}
              title="Refresh logs"
            >
              {refreshing ? (
                <>
                  <span style={{
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid #ffffff',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Refreshing...
                </>
              ) : (
                <>
                  <span>↻</span>
                  Refresh
                </>
              )}
            </button>
          </div>
          <div style={{ 
            display: 'flex', 
            gap: '15px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                Start Date:
              </label>
              <input
                type="date"
                value={dateFilter.startDate}
                onChange={(e) => setDateFilter({...dateFilter, startDate: e.target.value})}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
                End Date:
              </label>
              <input
                type="date"
                value={dateFilter.endDate}
                onChange={(e) => setDateFilter({...dateFilter, endDate: e.target.value})}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'end', gap: '10px' }}>
              <button
                onClick={() => setDateFilter({startDate: '', endDate: ''})}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear Filters
              </button>
              {filteredHistory.length > 0 && (
                <button
                  onClick={exportAsCSV}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Export CSV
                </button>
              )}
            </div>
          </div>
          
          
          <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
            Showing {filteredHistory.length} of {history.length} records
          </div>
        </div>

        {/* Usage History Table */}
        {filteredHistory.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#666',
            backgroundColor: '#f8f9fa',
            borderRadius: '4px'
          }}>
            <p>No usage history found for the selected date range.</p>
            <p>Try adjusting your date filters or start using the Digipin services.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              backgroundColor: 'white',
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Timestamp</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Address</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Digipin</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Action</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Source</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Search Type</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Place ID</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Latitude</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #dee2e6' }}>Longitude</th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '1px solid #dee2e6', width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((record, index) => {
                  return (
                    <tr key={index} style={{ 
                      borderBottom: '1px solid #f1f1f1'
                    }}>
                      <td style={{ padding: '12px' }}>
                        {new Date(record.timestamp).toLocaleString()}
                      </td>
                      <td style={{ padding: '12px', maxWidth: '300px', wordWrap: 'break-word' }}>
                        {record.address}
                      </td>
                      <td style={{ padding: '12px', fontfamily: 'monospace' }}>
                        {record.digipin}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          backgroundColor: record.place_id ? '#e3f2fd' : 
                                       record.action === 'lookup' ? '#d4edda' : 
                                       record.action === 'reverse' ? '#d1ecf1' : '#fff3cd',
                          color: record.place_id ? '#1565c0' :
                                record.action === 'lookup' ? '#155724' : 
                                record.action === 'reverse' ? '#0c5460' : '#856404'
                        }}>
                          {record.place_id ? 'Point of Interest' : record.action}
                        </span>
                      </td>
                      <td style={{ padding: '12px', textTransform: 'capitalize' }}>
                        {record.source ? record.source.replace(/_/g, ' ') : ''}
                      </td>
                      <td style={{ padding: '12px', textTransform: 'capitalize' }}>
                        {record.search_type || '-'}
                      </td>
                      <td style={{ padding: '12px', fontfamily: 'monospace', fontSize: '12px' }}>
                        {record.place_id || '-'}
                      </td>
                      <td style={{ padding: '12px', fontfamily: 'monospace', fontSize: '12px' }}>
                        {record.latitude || '-'}
                      </td>
                      <td style={{ padding: '12px', fontfamily: 'monospace', fontSize: '12px' }}>
                        {record.longitude || '-'}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <PdfButton
                          onClick={() => {
                            const recordId = `${record.id}_${index}`;
                            generateSinglePDF(record, recordId);
                          }}
                          loading={pdfLoading[`${record.id}_${index}`]}
                          disabled={pdfLoading[`${record.id}_${index}`]}
                          showQRPreview={!!(record.latitude && record.longitude)}
                          qrData={record.latitude && record.longitude ? `https://maps.google.com/maps?q=${record.latitude},${record.longitude}&z=15` : null}
                          title="Generate PDF Evidence with QR Code"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {/* Pagination Controls */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginTop: '20px',
              padding: '10px 0'
            }}>
              <div style={{ fontSize: '14px', color: '#666' }}>
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
              </div>
              
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ccc',
                    backgroundColor: currentPage === 1 ? '#f8f9fa' : 'white',
                    color: currentPage === 1 ? '#6c757d' : '#333',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  Previous
                </button>
                
                <span style={{ fontSize: '12px', color: '#666' }}>
                  Page {currentPage} of {Math.ceil(totalCount / pageSize)}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalCount / pageSize), prev + 1))}
                  disabled={currentPage >= Math.ceil(totalCount / pageSize)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ccc',
                    backgroundColor: currentPage >= Math.ceil(totalCount / pageSize) ? '#f8f9fa' : 'white',
                    color: currentPage >= Math.ceil(totalCount / pageSize) ? '#6c757d' : '#333',
                    cursor: currentPage >= Math.ceil(totalCount / pageSize) ? 'not-allowed' : 'pointer',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <p>Loading usage history...</p>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <div style={{ 
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        margin: 0,
        padding: '20px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <h2 style={{ margin: '0 0 20px 0', fontSize: '20px' }}>Usage History</h2>

      {error && (
        <div style={{ 
          padding: '10px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '1px solid #dee2e6',
        marginBottom: '20px'
      }}>
        <button
          onClick={() => setActiveTab('summary')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'summary' ? '#007bff' : 'transparent',
            color: activeTab === 'summary' ? 'white' : '#333',
            border: 'none',
            cursor: 'pointer',
            borderBottom: activeTab === 'summary' ? '2px solid #007bff' : 'none',
            fontWeight: activeTab === 'summary' ? 'bold' : 'normal'
          }}
        >
          Summary
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          style={{
            padding: '10px 20px',
            backgroundColor: activeTab === 'logs' ? '#007bff' : 'transparent',
            color: activeTab === 'logs' ? 'white' : '#333',
            border: 'none',
            cursor: 'pointer',
            borderBottom: activeTab === 'logs' ? '2px solid #007bff' : 'none',
            fontWeight: activeTab === 'logs' ? 'bold' : 'normal'
          }}
        >
          Logs
        </button>
      </div>

      {/* Tab Content */}
      <div style={{ 
        flex: 1, 
        overflow: 'hidden'
      }}>
        {activeTab === 'summary' ? renderSummaryTab() : renderLogsTab()}
      </div>
    </div>
    </>
  );
};

export default UsageHistory; 